import React, { FC, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { lookupCrediflowPartyTemplate } from 'apollo/templates/crediflow-party-lookup';
import { CrediflowParty, CrediflowPartyLookupResult, CrediflowSupportedCountry } from 'models/crediflow-party-lookup';
import Loader from 'components/shared/loader/loader.component';

const SUPPORTED_COUNTRIES: CrediflowSupportedCountry[] = [
    CrediflowSupportedCountry.SE,
    CrediflowSupportedCountry.NO,
    CrediflowSupportedCountry.DK,
    CrediflowSupportedCountry.FI,
];

const CrediflowPartyLookup: FC = () => {
    const { t: translate } = useTranslation(['app-settings', 'common', 'country-names']);
    const [organizationNumber, setOrganizationNumber] = useState('');
    const [countryCode, setCountryCode] = useState<CrediflowSupportedCountry>(CrediflowSupportedCountry.SE);

    const [lookup, { data, loading, error }] = useLazyQuery<{
        lookupCrediflowParty: CrediflowPartyLookupResult;
    }>(lookupCrediflowPartyTemplate, {
        fetchPolicy: 'network-only',
    });

    const handleLookup = () => {
        const trimmed = organizationNumber.trim();
        if (!trimmed) return;
        lookup({ variables: { organizationNumber: trimmed, countryCode } });
    };

    const result = data?.lookupCrediflowParty;

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h1" gutterBottom>
                {translate('app-settings:CREDIFLOW_PARTY_LOOKUP')}
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
                {translate('app-settings:ORGANIZATION_NUMBER_NO_SPECIAL_CHARS')}
            </Alert>
            <Box display="flex" gap={2} mb={3} alignItems="center" flexWrap="wrap">
                <TextField
                    label={translate('app-settings:ORGANIZATION_NUMBER')}
                    value={organizationNumber}
                    onChange={(event) => setOrganizationNumber(event.target.value)}
                    size="small"
                />
                <TextField
                    select
                    label={translate('app-settings:COUNTRY')}
                    value={countryCode}
                    onChange={(event) => setCountryCode(event.target.value as CrediflowSupportedCountry)}
                    size="small"
                    sx={{ minWidth: 180 }}
                >
                    {SUPPORTED_COUNTRIES.map((code) => (
                        <MenuItem key={code} value={code}>
                            {translate(`country-names:COUNTRY_${code}`)}
                        </MenuItem>
                    ))}
                </TextField>
                <Button variant="contained" onClick={handleLookup} disabled={!organizationNumber.trim() || loading}>
                    {translate('app-settings:LOOKUP')}
                </Button>
            </Box>

            {loading && <Loader />}

            {error && !loading && (
                <Typography color="error" sx={{ mt: 2 }}>
                    {error.message}
                </Typography>
            )}

            {result && !loading && !error && (
                <Box mt={2}>
                    <Typography variant="subtitle1" gutterBottom>
                        {translate('app-settings:RESULT')} ({result.totalResources})
                    </Typography>
                    {result.parties.length === 0 ? (
                        <Typography color="text.secondary">{translate('app-settings:NO_PARTIES_FOUND')}</Typography>
                    ) : (
                        result.parties.map((party, index) => <PartyCard key={party.id ?? index} party={party} />)
                    )}
                </Box>
            )}
        </Paper>
    );
};

type PartyCardProps = { party: CrediflowParty };

const PartyCard: FC<PartyCardProps> = ({ party }) => {
    const { t: translate } = useTranslation(['app-settings']);
    const detailRows: Array<[string, string | null | undefined]> = [
        [translate('app-settings:CREDIFLOW_PARTY_ID'), party.id],
        [translate('app-settings:ORGANIZATION_NUMBER'), party.cin],
        [translate('app-settings:COUNTRY'), party.countryCode],
        [translate('app-settings:NAME'), party.name],
        [translate('app-settings:CONTACT_NAME'), party.contactName],
        [translate('app-settings:EMAIL'), party.email],
        [translate('app-settings:ADDRESS'), [party.address1, party.address2].filter(Boolean).join(', ') || null],
        [translate('app-settings:ZIP_CODE'), party.zipCode],
        [translate('app-settings:CITY'), party.city],
        [translate('app-settings:PHONE'), party.phone],
        [translate('app-settings:GLN'), party.gln],
        [translate('app-settings:VAT'), party.vat],
    ];

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Table size="small">
                <TableBody>
                    {detailRows.map(([label, value]) => (
                        <TableRow key={label}>
                            <TableCell sx={{ width: 200, fontWeight: 500 }}>{label}</TableCell>
                            <TableCell>{value || '—'}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {party.services.length > 0 && (
                <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                        {translate('app-settings:SERVICES')}
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('app-settings:SERVICE_TYPE')}</TableCell>
                                <TableCell>{translate('app-settings:COMPLIANCE')}</TableCell>
                                <TableCell>{translate('app-settings:INSTRUCTIONS')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {party.services.map((service, index) => (
                                <TableRow key={`${service.serviceTypeId}-${index}`}>
                                    <TableCell>{service.serviceTypeId}</TableCell>
                                    <TableCell>{service.compliance}</TableCell>
                                    <TableCell>{service.instructions || '—'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}
        </Paper>
    );
};

export default CrediflowPartyLookup;
